import { supabase } from "@/integrations/supabase/client";
import { Category } from "@/types";
import { Database } from "@/integrations/supabase/types";

export const fetchCategories = async (): Promise<Category[]> => {
  // Query that joins categories with books_categories to count books per category
  const { data, error } = await supabase
    .from("categories")
    .select(
      `
      *,
      books_count:books_categories(count)
    `
    )
    .order("position", { ascending: true });

  if (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }

  if (!data) {
    return [];
  }

  // Convert from Supabase format to our app format
  return data.map((item) => {
    // Get the books count - safely handle undefined or null
    const booksCount =
      Array.isArray(item.books_count) && item.books_count.length > 0
        ? item.books_count[0]?.count || 0
        : 0;

    return {
      id: item.id,
      name: item.name,
      name_mz: (item as any).name_mz || undefined, // Handle name_mz field
      icon: item.icon || undefined,
      position: item.position || undefined,
      is_active: item.is_active,
      isActive: item.is_active, // Alias for frontend compatibility
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      booksCount,
    };
  });
};

export const createCategory = async (
  name: string,
  name_mz?: string,
  icon?: string,
  position?: number
): Promise<Category> => {
  const categoryData: any = { name, icon, position, is_active: true };
  if (name_mz) {
    categoryData.name_mz = name_mz;
  }

  const { data, error } = await supabase
    .from("categories")
    .insert(categoryData)
    .select()
    .single();

  if (error) {
    console.error("Error creating category:", error);
    throw error;
  }

  return {
    id: data.id,
    name: data.name,
    name_mz: (data as any).name_mz || undefined,
    icon: data.icon || undefined,
    position: data.position || undefined,
    is_active: data.is_active,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const updateCategory = async (
  id: number,
  updates: Partial<Category>
): Promise<Category> => {
  // Convert from our format to Supabase format
  type SupabaseCategoryUpdates =
    Database["public"]["Tables"]["categories"]["Update"];

  const supabaseUpdates: any = {};
  if (updates.name !== undefined) supabaseUpdates.name = updates.name;
  if (updates.name_mz !== undefined) supabaseUpdates.name_mz = updates.name_mz;
  if (updates.icon !== undefined) supabaseUpdates.icon = updates.icon;
  if (updates.position !== undefined)
    supabaseUpdates.position = updates.position;
  if (updates.is_active !== undefined)
    supabaseUpdates.is_active = updates.is_active;
  if (updates.isActive !== undefined)
    supabaseUpdates.is_active = updates.isActive;

  const { data, error } = await supabase
    .from("categories")
    .update(supabaseUpdates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating category:", error);
    throw error;
  }

  return {
    id: data.id,
    name: data.name,
    name_mz: (data as any).name_mz || undefined,
    icon: data.icon || undefined,
    position: data.position || undefined,
    is_active: data.is_active,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const toggleCategoryActiveState = async (
  id: number,
  isActive: boolean
): Promise<Category> => {
  return updateCategory(id, { is_active: isActive });
};

export const deleteCategory = async (id: number): Promise<void> => {
  const { error } = await supabase.from("categories").delete().eq("id", id);

  if (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
};
